import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { getOffsetParams } from './utils';

type UseSlidesType = PropsWithChildren & {
  allowScroll?: boolean;
  velocity?: number;
  step?: number;
  children: any;
};

export const useSlides = ({
  allowScroll = false,
  velocity = 0,
  step = 1,
  children,
}: UseSlidesType) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [slidesOffset, setSlidesOffset] = useState(0);
  const [canSlide, setCanSlide] = useState({ back: false, forward: false });

  const carouselRef = useRef(null);

  const itemsCount = Array.isArray(children) ? children.flat().length : 0;

  // Handle interaction by arrows
  useEffect(() => {
    if (allowScroll || !carouselRef.current) {
      return;
    }

    const { offset, maxOffset } = getOffsetParams(carouselRef, slideIndex);

    setSlidesOffset(offset);
    setCanSlide({ back: offset > 0, forward: offset < maxOffset });
  }, [allowScroll, slideIndex, itemsCount]);

  // Handle interaction by swipe
  useEffect(() => {
    if (!allowScroll || !carouselRef.current || !itemsCount) {
      return;
    }

    // `Math.ceil()` rounds up value to the nearest int greater than or equal to initial number.
    // However if `velocity` is -0.2 it will round up to 0 while we need -1.
    // Thus we first need to convert `velocity` to an absolute value and then
    // based on whether its a positive or negative number calculate nextIndex.
    const slidesCount = Number(Math.ceil(Math.abs(velocity)).toFixed());
    const nextIndex = velocity < 0 ? slideIndex + slidesCount : slideIndex - slidesCount;

    // Ensure that index is in bounds of 0 and amount of items.
    const currIndex = Math.min(Math.max(nextIndex, 0), itemsCount - 1);

    const { offset } = getOffsetParams(carouselRef, currIndex);

    setSlidesOffset(offset);
    setSlideIndex(currIndex);
  }, [allowScroll, velocity, itemsCount]);

  const slide = (asc) => setSlideIndex(asc ? slideIndex + step : slideIndex - step);

  const containerStyle = {
    transform: `matrix(1,0,0,1,-${slidesOffset},0)`,
  };

  return {
    slideBack: () => slide(false),
    slideForward: () => slide(true),
    canSlideBack: canSlide.back,
    canSlideForward: canSlide.forward,
    carouselRef,
    style: containerStyle,
  };
};
